/**
 * useApplyReturnNudge — Assisted-Handoff return detection.
 *
 * Strategy
 * --------
 * The user clicks Apply → we open the portal in a new tab → they go off and
 * (hopefully) submit on the portal → they come back to our tab. This hook
 * watches for that return and surfaces a "Did you finish applying?" prompt
 * so the user can mark the row as `applied` or `not_applied` without
 * having to navigate to My Activities.
 *
 * The detection is **dwell-aware**:
 *   - We only consider the user "returned" after they were on another tab
 *     for at least `minDwellMs` (default 30s). This avoids false-positives
 *     from accidental Alt-Tab, "check my notifications" flicks, or the
 *     PDF download briefly stealing focus.
 *   - We give up after `maxWaitMs` (default 30 minutes) so a forgotten
 *     prompt never lingers across a long break.
 *
 * No state is persisted across page reloads — if the user closes the tab
 * before the nudge fires, they can still confirm the application from My
 * Activities (which already supports updateApplicationStatus).
 *
 * Usage
 * -----
 *   const nudge = useApplyReturnNudge();
 *   // on Apply click, after the portal opens:
 *   nudge.arm({ applicationId, jobTitle, company });
 *   // render: nudge.pending && <ReturnNudgePrompt {...nudge.pending} onResolve={nudge.resolve} />
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { updateApplicationStatus } from '../../../services/applicationTrackingService';
import type { JobApplication } from '../../../services/applicationTrackingService';
import { pushUserNotification } from '../../../services/objectivesTasksService';
import { getCurrentUser } from '../../../utils/authUtils';

export interface ArmedNudge {
  applicationId: string;
  jobTitle: string;
  company: string;
  armedAt: number;
}

export interface ReturnNudgeApi {
  /** The currently visible nudge prompt, or null if none. */
  pending: ArmedNudge | null;
  /** Arm a new nudge after the user clicks Apply and the portal tab opens. */
  arm: (a: { applicationId: string; jobTitle: string; company: string }) => void;
  /** User confirmed an outcome — persists the status change and clears the prompt. */
  resolve: (outcome: 'applied' | 'not_applied' | 'later') => Promise<void>;
  /** Manually dismiss without persisting any status change. */
  dismiss: () => void;
}

interface NudgeOptions {
  /** Minimum time the user must be on another tab before we trust the return. */
  minDwellMs?: number;
  /** Maximum time we keep watching before giving up. */
  maxWaitMs?: number;
}

export function useApplyReturnNudge(options: NudgeOptions = {}): ReturnNudgeApi {
  const { minDwellMs = 30_000, maxWaitMs = 30 * 60_000 } = options;

  const [pending, setPending] = useState<ArmedNudge | null>(null);

  // Tracking state is kept in refs so the visibility listener doesn't have to
  // re-attach every render. Only the visible prompt lives in React state.
  const armedRef = useRef<ArmedNudge | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const confirmNotifiedRef = useRef<string | null>(null);

  const clearArm = useCallback(() => {
    armedRef.current = null;
    hiddenAtRef.current = null;
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const arm = useCallback<ReturnNudgeApi['arm']>(
    ({ applicationId, jobTitle, company }) => {
      if (!applicationId) return;
      const next: ArmedNudge = {
        applicationId,
        jobTitle: jobTitle || 'this role',
        company: company || 'this company',
        armedAt: Date.now(),
      };
      armedRef.current = next;
      // If the user happens to already be on another tab when we arm
      // (rare — usually they're focused on us when they click Apply),
      // start the dwell clock from now.
      hiddenAtRef.current =
        typeof document !== 'undefined' && document.visibilityState === 'hidden'
          ? Date.now()
          : null;

      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        // Give-up window — clear the arm so a stale prompt never appears
        // hours later when the user finally tabs back.
        clearArm();
      }, maxWaitMs);
    },
    [clearArm, maxWaitMs],
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const onVisibility = () => {
      const armed = armedRef.current;
      if (!armed) return;

      if (document.visibilityState === 'hidden') {
        // User just left our tab — start the dwell clock.
        hiddenAtRef.current = Date.now();
        return;
      }

      // visible: user is back. Check the dwell.
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt === null) return;

      const dwellMs = Date.now() - hiddenAt;
      if (dwellMs < minDwellMs) {
        // Quick tab-flick — ignore.
        return;
      }

      // Surface the prompt — armedRef is reset on resolve/dismiss.
      setPending(armed);
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [minDwellMs]);

  // Bell tray: one notification when the return prompt appears (deduped per application).
  useEffect(() => {
    if (!pending) return;
    if (confirmNotifiedRef.current === pending.applicationId) return;
    confirmNotifiedRef.current = pending.applicationId;
    void (async () => {
      const user = await getCurrentUser();
      if (!user?.id) return;
      await pushUserNotification(user.id, {
        type: 'apply_confirm_needed',
        entity_type: 'job_application',
        entity_id: pending.applicationId,
        message: `Did you finish applying to ${pending.jobTitle} at ${pending.company}? Use the prompt in Agent or My Activities to confirm.`,
      });
    })();
  }, [pending]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const resolve = useCallback<ReturnNudgeApi['resolve']>(
    async (outcome) => {
      const current = pending;
      if (!current) return;

      // Optimistically clear so the user gets immediate feedback.
      setPending(null);

      if (outcome === 'later') {
        // Keep the arm alive so we can re-prompt on the next return.
        return;
      }

      clearArm();

      const status: JobApplication['application_status'] =
        outcome === 'applied' ? 'applied' : 'not_applied';

      try {
        await updateApplicationStatus(
          current.applicationId,
          status,
          outcome === 'applied'
            ? 'Confirmed via Assisted Handoff return-nudge.'
            : 'User reported they did not submit on the portal.',
        );
      } catch (err) {
        console.warn('[useApplyReturnNudge] resolve failed:', err);
      }
    },
    [pending, clearArm],
  );

  const dismiss = useCallback<ReturnNudgeApi['dismiss']>(() => {
    setPending(null);
    confirmNotifiedRef.current = null;
    clearArm();
  }, [clearArm]);

  return { pending, arm, resolve, dismiss };
}

export default useApplyReturnNudge;
